import { c as clientGet, a as clientPost, p as processBody, v as voBanners, b as voBannersSchema, d as voucherValidatedOrder, e as voucherValidatedOrderSchema, f as clientPut, r as returnStatus, u as userAdsSchema, s as searchSchema, g as gallerizedSearchSchema, h as searchRecommendedSchema, i as filterValuesSchema, j as filterConfigSchema, k as userEmails, l as userEmailsHistorySchema, m as prices, n as pricesSchema, o as createdTransactionWithFlowURL, q as createdTransactionWithFlowUrlSchema, t as userContactInfo, w as userContactInfoSchema, x as regionsSchema, z as zonesSchema, y as geoEntriesSchema, A as userAdInfo, B as userAdInfoSchema, C as clientPostForm, D as conversationID, E as conversationIDSchema, F as authToken, G as messagingMessageResponse, H as messagingAttachment, I as uploadMessagingAttachmentSchema, J as messagingTriggerActionsResponse, K as unreadMessages, L as unreadMessagesSchema, M as channelIDBody, N as channelIDBodySchema, O as shops, P as shopsSchema, Q as fetchAndProcessErrors, R as payoutMethods, S as payoutMethodsSchema, T as voGroups, U as voGroupsSchema, V as publicationFee, W as pfGroups, X as pfGroupsSchema, Y as clientDelete, Z as savedSearchSchema, _ as savedSearchesSchema, $ as changeEmailProcess, a0 as changeEmailProcessSchema, a1 as changeEmail2FAChallenge, a2 as changeEmail2FAChallengeSchema, a3 as createStrictSchema, a4 as payoutUserSchema, a5 as correlatedAdsIdsSchema, a6 as paidOptionsPromoSchema, a7 as userProfileReverseSchema, a8 as clientPatch, a9 as userProfileInfo, aa as userProfileInfoSchema, ab as userProfile, ac as userProfileSchema, ad as poCartRedirect } from './zod-BK1RGncv.js';
import { u as userAds, s as searchResult, a as searchRecommendedResult, f as filterValues, b as filterConfig, g as geoRegions, c as geoZones, d as geoEntries, e as savedSearch, h as savedSearches, i as correlatedAdsIds } from './zod-DfBqeaiX.js';
import { morphism } from 'morphism';
import { z } from 'zod';
import { N as NoContentResponse } from './fixtures-DJBl6G8R.js';
import { HTTPStatusCode } from './types.js';
import 'zod-validation-error';

const getActiveVoBanners = (origin, entry_point, page, categories = []) => clientGet({
    url: `${origin}/v1/vouchers/banners`,
    queryParams: { entry_point, page, categories: categories.join(',') },
}).then(processBody(voBannersSchema, voBanners));
const validateVoucher = (origin, code, body, timeoutMs) => clientPost({
    url: `${origin}/v1/vouchers/validate/${code}`,
    timeoutMs,
}, body).then(processBody(voucherValidatedOrderSchema, voucherValidatedOrder));

const prolongAd = (origin, adID, userID, source) => clientPut({
    url: `${origin}/v1/prolong/user/${userID}/item/${adID}?source=${source}`,
}).then(returnStatus);
const getUserAds = (origin, userID, params, timeoutMs, isProd = false) => clientGet({
    url: `${origin}/v1/manage/users/${userID}/items`,
    queryParams: params,
    timeoutMs,
}).then(processBody(userAdsSchema, isProd ? null : userAds));

const searchItems = (origin, params, environmentID, isProd = false) => clientGet({
    url: `${origin}/v1/search/items`,
    queryParams: params,
    sbtHeaders: { environmentID },
}).then(processBody(searchSchema, isProd ? null : searchResult));
const searchGallerized = (origin, params, environmentID, isProd = false) => clientGet({
    url: `${origin}/v1/search/items/gallerized`,
    queryParams: {
        ...params,
        lim: params.lim ?? 3,
    },
    sbtHeaders: { environmentID },
}).then(processBody(gallerizedSearchSchema, isProd ? null : searchResult));
const searchGeoboosted = (origin, params, environmentID, isProd = false) => clientGet({
    url: `${origin}/v1/search/items/geoboosted`,
    queryParams: {
        ...params,
        lim: params.lim ?? 5,
    },
    sbtHeaders: { environmentID },
}).then(processBody(searchSchema, isProd ? null : searchResult));
const searchRecommendedHp = (origin, params) => clientGet({
    url: `${origin}/v1/search/items/hp-recommended`,
    queryParams: { ...params, lim: params.lim ?? 20 },
}).then(processBody(searchSchema, searchResult));
const searchRecommendedDetail = (origin, urn) => clientGet({
    url: `${origin}/v1/search/items/detail-recommended`,
    queryParams: { urn, lim: 20 },
}).then(processBody(searchSchema, searchResult));
const searchRecommended = (origin, urn) => clientGet({
    url: `${origin}/v1/search/items/pro/recommender?urn=${urn}`,
}).then(processBody(searchRecommendedSchema, searchRecommendedResult));
const getFilterValues = (origin, uri, category) => {
    if (!uri.startsWith('/v1/values')) {
        return Promise.reject(new Error('Missing or invalid URI parameter'));
    }
    return clientGet({
        url: `${origin}${uri}`,
        queryParams: {
            category,
        },
    }).then(processBody(filterValuesSchema, filterValues));
};
const getFiltersConfig = (origin, categoryId, adType, timeoutMs, isProd = false) => {
    return clientGet({
        url: `${origin}/v1/search/categories/${categoryId}/ad_types/${adType}/filters`,
        timeoutMs,
    }).then(processBody(filterConfigSchema, isProd ? null : filterConfig));
};

const historyUserEmail = (origin, userId) => clientGet({
    url: `${origin}/v1/private/accounts/${userId}/email-history`,
}).then(processBody(userEmailsHistorySchema, userEmails));

const getPricesPreview = (origin, transactionType, transactionID, offerPrice) => {
    const query = offerPrice ? `?item_price=${offerPrice}` : '';
    return clientGet({
        url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/prices/preview${query}`,
    }).then(processBody(pricesSchema, prices));
};
const sendBuyerOffer = (origin, transactionType, transactionID, offerPrice) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/request_purchase`,
}, { item_price: offerPrice }).then(returnStatus);
const cancelBuyerOffer = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/cancel`,
}).then(returnStatus);
const acceptBuyerOffer = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/accept_purchase`,
}).then(returnStatus);
const refuseOffer = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/refuse_purchase`,
}).then(returnStatus);
const sendSellerOffer = (origin, transactionType, transactionID, offerPrice) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/seller_request_purchase`,
}, { item_price: offerPrice }).then(returnStatus);
const cancelSellerOffer = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/seller_cancel`,
}).then(returnStatus);
const confirmAvailability = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/confirm_availability`,
}).then(returnStatus);
const signalItemIssue = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/buyer_item_issue`,
}).then(returnStatus);
const sellerRefundItemIssue = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/seller_cancel_item_issue`,
}).then(returnStatus);
const openDispute = (origin, transactionType, transactionID, issueType, body) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/open_dispute/${issueType}`,
}, body).then(returnStatus);
const sellerCancelDispute = (origin, transactionType, transactionID) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionType}/instances/${transactionID}/actions/seller_cancel_dispute`,
}).then(returnStatus);
const sendReturn = (origin, transactionID, body) => clientPost({
    url: `${origin}/v1/p2p/transactions/${transactionID}/return`,
}, body).then(returnStatus);
const createTransaction = (origin, itemID, itemType, isBuyNow) => clientPost({ url: `${origin}/v1/p2p/transactions` }, {
    item_id: itemID,
    item_type: itemType,
    ...(isBuyNow !== undefined ? { cta: isBuyNow ? 'buy' : 'offer' } : {}),
}).then(processBody(createdTransactionWithFlowUrlSchema, createdTransactionWithFlowURL));

const getContactInfo = (origin, adURN, timeout) => clientGet({
    url: `${origin}/v1/contacts/ads/${adURN}`,
    timeoutMs: timeout,
}).then(processBody(userContactInfoSchema, userContactInfo));

const postComplaint = (origin, adUrn, complaintDetails, email, token) => clientPost({
    url: `${origin}/v1/review/complaints`,
    sbtHeaders: {
        authToken: token,
    },
}, {
    ...complaintDetails,
    ad_urn: adUrn,
    ...(email ? { email } : {}),
}).then(returnStatus);

const getRegions = (origin) => clientGet({
    url: `${origin}/v1/geo/regions`,
}).then(processBody(regionsSchema, geoRegions));
const getZones = (origin, town) => clientGet({
    url: `${origin}/v1/geo/regions/${town.regionId}/cities/${town.cityId}/towns/${town.id}/zones`,
}).then(processBody(zonesSchema, geoZones));
const geoSearch = (origin, key, query) => clientGet({
    url: `${origin}/v1/geo/search`,
    queryParams: {
        key,
        ...query,
    },
}).then(processBody(geoEntriesSchema, geoEntries));

const getUserAdInfo = (origin, userId, timeout) => clientGet({
    url: `${origin}/v1/accounts/search/${userId}/info`,
    timeoutMs: timeout,
}).then(processBody(userAdInfoSchema, userAdInfo));

const infoBuyerAdReplySchema = {
    email: 'email',
    forwardPhone: 'forward_phone',
    phone: 'phone',
    name: 'name',
    userId: 'user_id',
};
const buyerInfoAdReplySchema = {
    buyerInfo: {
        path: 'data',
        fn: morphism(infoBuyerAdReplySchema),
    },
};
const infoAdReplySchema = {
    email: 'email',
    name: 'name',
    body: 'body',
    cc: 'cc',
    acceptTermsConds: 'accept_terms_conds',
    phone: 'phone',
    forwardPhone: 'forward_phone',
};
const adReplySchema = {
    reply: {
        path: 'data',
        fn: morphism(infoAdReplySchema),
    },
};

const infoBuyerAdReply = z.object({
    userId: z.string(),
    email: z.string(),
    name: z.string(),
    forwardPhone: z.boolean().optional(),
    phone: z.string().optional(),
});
const infoAdReply = z.object({
    email: z.string(),
    name: z.string(),
    body: z.string(),
    cc: z.boolean(),
    acceptTermsConds: z.boolean(),
    phone: z.string().optional(),
    forwardPhone: z.boolean().optional(),
});
const buyerInfoAdReply = z.object({
    buyerInfo: infoBuyerAdReply.optional(),
});
const adReply = z.object({
    reply: infoAdReply.optional(),
});
z.object({
    name: z.string(),
    body: z.string(),
    accept_terms_conds: z.boolean(),
    cc: z.boolean(),
    forward_phone: z.boolean().optional(),
    phone: z.string().optional(),
});

const getBuyerInfo = (origin, userID) => clientGet({
    url: `${origin}/v1/reply/users/${userID}/info`,
}).then(processBody(buyerInfoAdReplySchema, buyerInfoAdReply));
const sendAdReply = (origin, adID, body, buyerInfo, attachment) => {
    const req = {
        accept_terms_conds: true,
        cc: false,
        name: buyerInfo.name,
        phone: buyerInfo.phone,
        forward_phone: buyerInfo.forwardPhone,
        body,
    };
    if (attachment) {
        const data = new FormData();
        data.append('request', new Blob([JSON.stringify(req)], {
            type: 'application/json',
        }), 'request');
        data.append('attach', attachment);
        return clientPostForm({ url: `${origin}/v1/reply/${adID}` }, data).then(processBody(adReplySchema, adReply));
    }
    else {
        return clientPost({ url: `${origin}/v1/reply/${adID}` }, req).then(processBody(adReplySchema, adReply));
    }
};
const getConversationID = (origin, userID, partnerID, itemID) => clientGet({
    url: `${origin}/v1/mcproxy/api/hal/${userID}/conversations?partnerId=${partnerID}&itemId=${itemID}&itemType=ad`,
}).then(processBody(conversationIDSchema, conversationID));
const getChatAuthToken = (origin, userID) => clientPost({ url: `${origin}/v1/chat/users/${userID}/auth/token` }).then(processBody(null, authToken));
//remove when the Stream post rollout is finished(new messaging)
const sendMessageToMessaging = (origin, userID, conversationID, body) => clientPost({
    url: `${origin}/v1/mcproxy/api/hal/${userID}/conversations/${conversationID}/messages`,
}, body).then(processBody(null, messagingMessageResponse));
const uploadMessagingAttachment = (origin, channelID, attachment) => {
    const formData = new FormData();
    formData.append('file', attachment);
    return clientPostForm({
        url: `${origin}/v1/chat/channel/${channelID}/attachment`,
    }, formData).then(processBody(uploadMessagingAttachmentSchema, messagingAttachment));
};
const getTriggerActions = (origin, userID, channelID) => clientGet({
    url: `${origin}/v1/chat/users/${userID}/channel/${channelID}/actions`,
}).then(processBody(null, messagingTriggerActionsResponse));
const getUnreadMessages = (origin, userID) => clientGet({
    url: `${origin}/v1/chat/messages/${userID}/unread`,
}).then(processBody(unreadMessagesSchema, unreadMessages));
const getChannelIdIfExists = (origin, userID, adID, peerID) => clientGet({
    url: `${origin}/v1/chat/users/${userID}/channel?ad_id=${adID}&peer_id=${peerID}`,
}).then(processBody(channelIDBodySchema, channelIDBody));

const searchShops = (origin, params, timeout) => clientGet({
    url: `${origin}/v1/shops-q`,
    queryParams: {
        ...params,
    },
    timeoutMs: timeout,
}).then(processBody(shopsSchema, shops));

const getPayoutTransferMethods = (origin, userToken, authToken) => fetchAndProcessErrors({ url: `${origin}/rest/v3/users/${userToken}/transfer-methods` }, {
    headers: { Authorization: `Bearer ${authToken}` },
}).then((res) => res.status === HTTPStatusCode.NoContent
    ? NoContentResponse([])
    : processBody(payoutMethodsSchema, payoutMethods)(res));

const getVisibilityOptions = (origin, ad_id, category) => clientGet({
    url: `${origin}/v1/payments/paid_options`,
    queryParams: {
        ad_id,
        category,
        entry_point: 'PROMOTE',
    },
}).then(processBody(voGroupsSchema, voGroups));
const getPublicationFee = (origin, categoryID) => clientGet({
    url: `${origin}/v1/payments/categories/${categoryID}/publication_fee`,
}).then(processBody(null, publicationFee));
const getPremiumFeatures = (origin, category, entry_point) => clientGet({
    url: `${origin}/v1/payments/premium_features`,
    queryParams: {
        entry_point,
        category,
    },
}).then(processBody(pfGroupsSchema, pfGroups));

const saveSearch = (origin, userID, search, channel) => clientPost({
    url: `${origin}/v1/searches-q/users/${userID}/searches`,
    channelOverride: channel,
}, search).then(processBody(savedSearchSchema, savedSearch));
const getUserSavedSearch = (origin, userID) => clientGet({ url: `${origin}/v1/searches-q/users/${userID}/searches` }).then(processBody(savedSearchesSchema, savedSearches));
const deleteSavedSearch = (origin, userID, searchID) => clientDelete({
    url: `${origin}/v1/searches-q/users/${userID}/searches/${searchID}`,
}).then(returnStatus);

const changeEmailPrivate = (origin, userId, email) => {
    return clientPut({
        url: `${origin}/v1/private/users/${userId}/change-email`,
    }, {
        email,
    });
};
const changeEmailSessionDetails = (origin, userId) => {
    return clientGet({
        url: `${origin}/v1/users/${userId}/change-email/session`,
    }).then(processBody(changeEmailProcessSchema, changeEmailProcess));
};
const createOrUpdateNewChangeEmailSession = (origin, userId, newEmail, action) => {
    return (action === 'create' ? clientPost : clientPut)({
        url: `${origin}/v1/users/${userId}/change-email/session`,
    }, { email: newEmail }).then(processBody(changeEmailProcessSchema, changeEmailProcess));
};
const changeEmailSubmit = (origin, userId, newEmail) => {
    return clientPost({
        url: `${origin}/v1/users/${userId}/change-email/submit`,
    }, { email: newEmail }).then(returnStatus);
};
const challenge2faForChangeEmailProcess = (origin, userId, challangeFor, type) => {
    const path = challangeFor === 'account' ? 'account' : 'new-email';
    return clientPost({
        url: `${origin}/v1/users/${userId}/change-email/challenge/${path}`,
    }, { type }).then(processBody(changeEmail2FAChallengeSchema, changeEmail2FAChallenge));
};

const survey = z.object({
    id: z.string(),
    medalliaID: z.string(),
    title: z.string(),
    description: z.string(),
});
const surveys = z.object({
    surveys: z.array(survey),
});

const surveySchema = createStrictSchema({
    id: 'id',
    medalliaID: 'medallia_id',
    title: 'title',
    description: 'description',
});
const surveysSchema = createStrictSchema({
    surveys: {
        path: 'surveys',
        fn: morphism(surveySchema),
    },
});

const getSurveys = (origin, page) => clientGet({ url: `${origin}/v1/surveys`, queryParams: { page } }).then(processBody(surveysSchema, surveys));
const updateSurveyStatus = (origin, surveyID, status) => clientPost({ url: `${origin}/v1/surveys/${surveyID}` }, { status }).then(returnStatus);

const doNotMorph = (t) => t;
const morph = async (parsedBody, morphismSchema) => {
    const morpher = morphismSchema ? morphism(morphismSchema) : doNotMorph;
    return morpher(parsedBody);
};

const upsertPayoutUser = (origin, userID, userInfo) => morph(userInfo, payoutUserSchema)
    .then((morphedBody) => clientPut({ url: `${origin}/v1/p2p/payments/users/${userID}` }, morphedBody))
    .then(returnStatus);

const getCorrelatedAdsIds = (origin, listID, userID, source) => clientGet({
    url: `${origin}/v1/correlated/correlated_items/${listID}/ids`,
    queryParams: {
        tracking_id: userID,
        source,
    },
}).then(processBody(correlatedAdsIdsSchema, correlatedAdsIds));

/**
 * Documentation: https://confluence.ets.mpi-internal.com/display/SBTDOC/Create+promo
 */
const createPromo = (origin, paidOptionsPromo) => morph(paidOptionsPromo, paidOptionsPromoSchema).then((morphedBody) => clientPost({ url: `${origin}/v1/private/promos` }, morphedBody).then(returnStatus));

const getUserProfileInfo = (origin, userID, timeout) => clientGet({
    url: `${origin}/v1/users/${userID}/info/profile/query`,
    timeoutMs: timeout,
}).then(processBody(userProfileInfoSchema, userProfileInfo));
const updateUserProfile = async (origin, userID, profile, method = 'put', timeout) => {
    const morphedBody = await morph(profile, userProfileReverseSchema);
    const endpointConf = {
        url: `${origin}/v1/users/${userID}/info/profile`,
        timeoutMs: timeout,
    };
    const clientRequest = method === 'put'
        ? clientPut(endpointConf, morphedBody)
        : clientPatch(endpointConf, morphedBody);
    return clientRequest.then(processBody(userProfileSchema, userProfile));
};

const createPoOrder = (origin, order) => clientPost({ url: `${origin}/carrello` }, {
    order,
}).then(processBody(null, poCartRedirect));

const adsense = z.object({
    sequence: z.number(),
    number_of_ads: z.number(),
    style_id: z.string().optional(),
    channel_id: z.string().optional(),
    size: z.string().optional(),
    max_term_length: z.number().optional(),
});
const bidder = z.object({
    name: z.string(),
    params: z.record(z.union([z.string(), z.number()])),
});
const sizeEnum = z.enum(['fluid']);
const size = z.union([
    sizeEnum,
    z.string().regex(/^\d+x\d+$/, 'value not valid'),
]);
const gam = z.object({
    sequence: z.number(),
    unit_id: z.string(),
    sizes: z.array(size),
    custom_native: z
        .object({
        style: z.string().optional(),
        template_id: z.string().optional(),
    })
        .optional(),
    template_native: z
        .object({
        style: z.string(),
    })
        .optional(),
    bidding_systems: z
        .array(z.object({
        name: z.string().optional(),
        bidders: z.array(bidder),
    }))
        .optional(),
    refresh: z
        .object({
        interval: z.number(),
        enabled: z.boolean(),
    })
        .optional(),
});
const pageSettings = z.object({
    adsense: z
        .object({
        style_id: z.string().optional(),
        channel_id: z.string().optional(),
        max_term_length: z.number().optional(),
        pub_id: z.string(),
        ad_page: z.number().optional(),
        adsafe: z.enum(['high', 'medium', 'low']).optional(),
        link_target: z.enum(['_blank', '_top']).optional(),
        hl: z.string().optional(),
    })
        .optional(),
    gam: z.object({ content_url: z.unknown() }).optional(),
});
const slot = z.object({
    container_id: z.string(),
    name: z.string(),
    show_when_unfilled: z.string().optional(),
    configurations: z.object({
        adsense: z.array(adsense).optional(),
        gam: z.array(gam).optional(),
    }),
});
const config = z.object({
    id: z.string(),
    version: z.number(),
    name: z.string(),
    property: z.string(),
    vertical: z.string(),
    active: z.boolean(),
    variant: z.string().optional(),
    slots: z.array(slot),
    page_level_settings: pageSettings,
});
z.object({
    variant: z.string().optional(),
    name: z.string().optional(),
    property: z.string().optional(),
    vertical: z.string().optional(),
    active: z.boolean().optional(),
});
const configsResult = z.array(config);
const configsPageResult = config;

const getConfigs = (origin, params) => clientGet({
    url: `${origin}/v1/private/adv/configs`,
    queryParams: params,
}).then(processBody(null, configsResult));
const getConfigPage = (origin, params) => clientGet({
    url: `${origin}/v1/adv/configs/page`,
    queryParams: params,
}).then(processBody(null, configsPageResult));
const updateConfig = (origin, config) => clientPost({
    url: `${origin}/v1/adv/configs/page`,
}, config).then(processBody(null, configsPageResult));
const activateConfig = (origin, id) => clientPut({
    url: `${origin}/v1/adv/configs/page`,
}, { id }).then(processBody(null, configsPageResult));

z.object({
    vehicleType: z.enum(['car', 'motorcycle']),
    plateNumber: z.string(),
});
const vehiclePreviewResponse = z.object({
    fuelType: z.string(),
    co2Emissions: z.number(),
    engineDisplacement: z.number(),
    euroStandard: z.string(),
    numberOfSeats: z.number(),
    powerKw: z.number(),
    transmissionType: z.string(),
    description: z.string(),
    registrationStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // ISO date string (e.g., "2015-01-01")
    licensePlate: z.string(),
    vehicleType: z.string(),
    stockImage: z.string().url(),
});
const vehicleInsertResponse = z.object({
    id: z.string(),
});

const vehiclePreviewSchema = createStrictSchema({
    fuelType: 'fuel_type',
    co2Emissions: 'co2_emissions',
    engineDisplacement: 'engine_displacement',
    euroStandard: 'euro_standard',
    numberOfSeats: 'number_of_seats',
    powerKw: 'power_kw',
    transmissionType: 'transmission_type',
    description: 'description',
    registrationStart: 'registration_start',
    licensePlate: 'license_plate',
    vehicleType: 'vehicle_type',
    stockImage: 'stock_image',
});

const getVehiclePreview = (origin, data) => clientGet({
    url: `${origin}/v1/vehicles/${data.vehicleType}/plate_numbers/${data.plateNumber}`,
    timeoutMs: 15000,
}).then(processBody(vehiclePreviewSchema, vehiclePreviewResponse));
const vehicleInsert = (origin, data) => clientPost({
    url: `${origin}/v1/garage/vehicles`,
}, data).then(processBody(null, vehicleInsertResponse));

export { acceptBuyerOffer, activateConfig, cancelBuyerOffer, cancelSellerOffer, challenge2faForChangeEmailProcess, changeEmailPrivate, changeEmailSessionDetails, changeEmailSubmit, confirmAvailability, createOrUpdateNewChangeEmailSession, createPoOrder, createPromo, createTransaction, deleteSavedSearch, geoSearch, getActiveVoBanners, getBuyerInfo, getChannelIdIfExists, getChatAuthToken, getConfigPage, getConfigs, getContactInfo, getConversationID, getCorrelatedAdsIds, getFilterValues, getFiltersConfig, getPayoutTransferMethods, getPremiumFeatures, getPricesPreview, getPublicationFee, getRegions, getSurveys, getTriggerActions, getUnreadMessages, getUserAdInfo, getUserAds, getUserProfileInfo, getUserSavedSearch, getVehiclePreview, getVisibilityOptions, getZones, historyUserEmail, openDispute, postComplaint, prolongAd, refuseOffer, saveSearch, searchGallerized, searchGeoboosted, searchItems, searchRecommended, searchRecommendedDetail, searchRecommendedHp, searchShops, sellerCancelDispute, sellerRefundItemIssue, sendAdReply, sendBuyerOffer, sendMessageToMessaging, sendReturn, sendSellerOffer, signalItemIssue, updateConfig, updateSurveyStatus, updateUserProfile, uploadMessagingAttachment, upsertPayoutUser, validateVoucher, vehicleInsert };
//# sourceMappingURL=client_index.js.map
